import type { TotpChallengeResponse } from '@rd/shared';
import { CircleAlert, Info, ShieldCheck } from 'lucide-react';
import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { useAuth } from '../auth/AuthProvider.js';
import { useBranding } from '../branding/BrandingProvider.js';
import { Button } from '../components/Button.js';
import { Field } from '../components/Field.js';
import { Input } from '../components/Input.js';
import { ApiClientError } from '../lib/apiClient.js';
import { copyFor, formatSeconds } from '../lib/errorCopy.js';

/**
 * The auth screens are browsed rather than worked in, so their fields are taller
 * than the console's 34px, and the box gets `line-strong` — on a tenant whose
 * surface and surface-alt are close, the default hairline leaves an empty input
 * with no visible edge at all.
 *
 * 44px rather than the 40px the password screens use. This is the one form a
 * stranger meets cold, on a phone as often as on a desk, and 44px is the size
 * at which a field stops being a line and starts being a control — it also
 * matches the submit button below it exactly, so the column reads as one
 * object. The reset/change screens draw their inputs from the shared
 * `PasswordFields` block and are deliberately left where they are; moving them
 * is a change to a component this pass does not own.
 *
 * `focus:border-primary` is the deliberate part. The global `:focus-visible`
 * ring in globals.css is the accessibility guarantee and is untouched; this is
 * the *typing* affordance — it fires on pointer focus too, so the field you are
 * in is obvious while you fill the form. It is a border, never a background:
 * no text sits on it, so its contrast cannot depend on the tenant's hue
 * (§16.3.1). It is the same treatment the active nav item uses, for the same
 * reason.
 */
const FIELD_CLASS = 'h-11 border-line-strong focus:border-primary';

/**
 * One primary control for both steps: full width, the field's own height, and
 * enough weight to be the obvious end of the column. `font-semibold` and
 * `shadow-[var(--elevation-2)]` override `Button`'s own `font-medium` and
 * `elevation-1` — a filled button on a card wants the card's elevation, not the
 * step below it — and both win by Tailwind's value ordering, the same mechanism
 * `h-11` already relies on to beat `Button`'s `h-9`.
 */
const SUBMIT_CLASS = 'mt-[var(--space-2)] h-11 w-full font-semibold shadow-[var(--elevation-2)]';

/**
 * Sign-in — plan E4, copy governed by §04.4.
 *
 * ┌────────────────────────────────────────────────────────────────────┐
 * │ The backend makes an unknown email and a wrong password            │
 * │ **byte-identical**, deliberately, and it is QA-verified. This form  │
 * │ must not undo that.                                                 │
 * │                                                                     │
 * │ Concretely, and all three matter:                                   │
 * │   * ONE message, above the form, for both cases. Never "we couldn't │
 * │     find that account", never an inline "email not recognised".     │
 * │   * BOTH fields marked invalid, NEITHER named as the culprit.       │
 * │   * **No client-side existence pre-check** — no "does this email    │
 * │     exist" request on blur. That would leak by network timing and   │
 * │     by the mere presence of the request, which is why criterion 14  │
 * │     asserts on the MSW request log and not only on the DOM.         │
 * └────────────────────────────────────────────────────────────────────┘
 *
 * ── Why this page looks different from every other one ────────────────
 *
 * It is the one screen a person *browses* rather than works in, so it is the
 * one screen allowed a display size (`--text-3xl`) and a decorative half. Two
 * properties of that layout are load-bearing rather than aesthetic:
 *
 *   * **The form column is FIRST in the DOM**, with the decorative panel
 *     second. No `order-*` swap, so source order and visual order agree — which
 *     matters because `e2e/shell.spec.ts` tabs at most twelve times to traverse
 *     this form, and because the axe run on `/login` is the accessibility gate
 *     for the whole suite. A decorative column that came first would spend tab
 *     stops and reading order on a picture.
 *   * **The panel is the only large tenant-coloured surface in the product**
 *     (`.brand-veil`, capped at 5% in globals.css, with `.brand-glow` over it
 *     lightening and shading that same wash without adding hue to it). It is
 *     `aria-hidden` and holds no focusable element. Every tenant-coloured layer
 *     in it carries **no text at all**: §16.3.1's rule is that a component may
 *     never put text on a ground whose value the tenant chooses, because the
 *     ratio then depends on their hue rather than on anything we measured.
 *
 *     The lockup is how the panel gets typography anyway, and it is the whole
 *     reason it is a solid plate rather than a floating word. `.halo` paints
 *     plain `surface` and the rings are cast *outside* it as box-shadow spreads,
 *     so the mark and the product name sit on `surface`/`body` — the pair
 *     `designTokens.spec.ts` measures at 17.75:1 — while the hue stays strictly
 *     behind them. Move that text off the plate and the guarantee is gone.
 */
export function LoginPage(): ReactElement {
  const { login, verifyTotp, status, endedReason } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tenantSlug, setTenantSlug] = useState(params.get('tenant') ?? '');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  /**
   * §B7: non-null after a correct password on a TOTP-enrolled account. The
   * card then shows the code step instead of the credential form — one screen,
   * two steps, because the challenge token is worth nothing to a bookmark and
   * a separate route would invite navigating to it cold.
   */
  const [challenge, setChallenge] = useState<TotpChallengeResponse | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);

  // The slug field appears only when no tenant resolved from the host. On a
  // customer's own hostname asking them which company they work for is noise.
  const needsTenantField = branding?.tenantRef === null;

  const sessionEnded =
    endedReason === 'session-expired' || params.get('reason') === 'session-expired';

  useEffect(() => {
    if (status === 'authenticated') {
      const from = (location.state as { from?: string } | null)?.from;
      void navigate(from ?? '/dashboard', { replace: true });
    }
  }, [status, navigate, location.state]);

  // A live countdown, so a disabled button explains itself rather than looking
  // broken. Never an automatic retry — a login is not something to replay.
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((n) => n - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await login({
        email,
        password,
        ...(needsTenantField && tenantSlug ? { tenantSlug } : {}),
      });
      // The union's challenge arm: password verified, session withheld until
      // a code arrives. The password is deliberately dropped from state here —
      // the second step needs only the challenge token.
      if (result) {
        setChallenge(result);
        setPassword('');
      }
    } catch (err) {
      setError(err);
      if (err instanceof ApiClientError && err.code === 'RATE_LIMITED') {
        setCooldown(err.retryAfterSeconds ?? 60);
      }
    } finally {
      setBusy(false);
    }
  };

  /** Abandons the challenge and returns to step one with clean fields. */
  const restart = (): void => {
    setChallenge(null);
    setCode('');
    setRecoveryCode('');
    setUseRecovery(false);
    setError(null);
  };

  const onVerify = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (challenge === null) return;
    setError(null);
    setBusy(true);
    try {
      // Exactly one factor — the schema is `.strict()` and refuses both.
      await verifyTotp(
        useRecovery
          ? { challengeToken: challenge.challengeToken, recoveryCode: recoveryCode.trim() }
          : { challengeToken: challenge.challengeToken, code: code.trim() },
      );
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'AUTH_TOTP_CHALLENGE_EXPIRED') {
        // The password step succeeded but the code took too long. The only
        // recovery is starting over, so go there — with the message, or the
        // reset reads as the app losing the form.
        restart();
        setError(err);
        return;
      }
      setError(err);
      if (err instanceof ApiClientError && err.code === 'RATE_LIMITED') {
        setCooldown(err.retryAfterSeconds ?? 60);
      }
    } finally {
      setBusy(false);
    }
  };

  const copy = error ? copyFor(error) : null;
  // Both fields, never one: the server cannot tell us which was wrong, and
  // marking only the email would be a guess the user reads as fact.
  const credentialsRejected =
    error instanceof ApiClientError && error.code === 'AUTH_INVALID_CREDENTIALS';

  return (
    /*
      Not `lg:grid-cols-2`. The reading column needs a measured 28rem card and
      nothing more; the brand column is a composition and takes the remainder,
      so the extra width goes where it does something. `minmax(0, …)` on both
      tracks because a grid track's default `min-width: auto` lets a long
      product name push the column wider than its share instead of wrapping.
    */
    <div className="grid min-h-screen bg-surface-alt lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
      {/*
        The reading column, first in the DOM. `<main>` rather than a bare div so
        the only announced content on the page sits inside a landmark — the
        panel opposite is hidden from the tree entirely.
      */}
      <main className="flex items-center justify-center px-[var(--space-6)] py-[var(--space-10)] lg:px-[var(--space-12)] lg:py-[var(--space-12)]">
        <div className="flex w-full max-w-md flex-col gap-[var(--space-6)] card p-[var(--space-8)] shadow-[var(--elevation-2)]">
          <div className="flex flex-col gap-[var(--space-5)]">
            {branding?.logoUrl ? (
              // Rendered at every width, unlike the decorative panel: this is
              // the copy of the mark that carries the accessible name, and the
              // alt text is the product name rather than "logo" — a screen
              // reader announcing "logo" tells the user nothing about whose
              // panel they are signing in to.
              <img
                src={branding.logoUrl}
                alt={branding.productName}
                className="h-9 w-auto self-start"
              />
            ) : null}
            {/*
              Two ranks inside ONE heading, which is the point. "Sign in to" is
              the instruction and the product name is the answer to "whose panel
              is this", and at one size and one weight the second loses to the
              first. Splitting them into two elements would split the heading;
              a `<span>` keeps the accessible name exactly "Sign in to
              {product}" — `text-transform` changes pixels, never the name — so
              every `getByRole('heading', { name: /sign in/i })` in the suite
              still matches, and a screen reader still hears one sentence.
            */}
            <h1 className="text-[length:var(--text-3xl)] font-semibold leading-[var(--leading-display)] tracking-[var(--tracking-tight)] text-body">
              <span className="mb-[var(--space-2)] block text-[length:var(--text-xs)] font-semibold uppercase tracking-[var(--tracking-caps)] text-muted">
                Sign in to
              </span>
              {branding?.productName ?? 'Connect Panel'}
            </h1>
          </div>

          {sessionEnded ? (
            <p
              role="status"
              className="flex items-start gap-[var(--space-2)] rounded-[var(--radius-md)] border border-line bg-surface-alt p-[var(--space-3)] text-sm text-body"
            >
              <Info
                aria-hidden="true"
                size={16}
                strokeWidth={2}
                className="mt-[2px] shrink-0 text-muted"
              />
              {/* §04.4: what ended, never why. The vocabulary of the refresh
                  mechanism is not something a signed-out person needs. */}
              Your session ended. Please sign in again.
            </p>
          ) : null}

          {challenge !== null ? (
            /*
              §B7 step two. Same card, same field metrics, same submit — this is
              still the sign-in, not a different screen. The credential form
              below never renders while a challenge is live, so the password
              cannot be resubmitted by accident.
            */
            <form
              onSubmit={(e) => void onVerify(e)}
              className="flex flex-col gap-[var(--space-5)]"
              noValidate
            >
              {/*
                The glyph is the only thing added to this step, and it is
                decoration: `aria-hidden`, no text node, neutral rather than
                `success` — nothing has succeeded yet. It exists so the step
                announces itself as a *second* step at a glance instead of
                reading as the first form having reloaded with different fields.
              */}
              <div className="flex items-start gap-[var(--space-3)]">
                <span
                  aria-hidden="true"
                  className="flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-md)] border border-line bg-surface-alt text-muted"
                >
                  <ShieldCheck size={18} strokeWidth={2} />
                </span>
                <p className="text-sm text-body">
                  {useRecovery
                    ? 'Enter one of the recovery codes you saved when you set up two-factor authentication.'
                    : 'Enter the 6-digit code from your authenticator app.'}
                </p>
              </div>

              <ErrorNotice copy={copy} testId="totp-error" />

              {useRecovery ? (
                <Field label="Recovery code" required>
                  {(props) => (
                    <Input
                      {...props}
                      name="recoveryCode"
                      autoComplete="off"
                      autoFocus
                      placeholder="XXXX-XXXX-XXXX-XXXX"
                      value={recoveryCode}
                      onChange={(e) => setRecoveryCode(e.target.value)}
                      className={`${FIELD_CLASS} text-center font-mono tracking-[0.12em]`}
                    />
                  )}
                </Field>
              ) : (
                <Field label="Verification code" required>
                  {(props) => (
                    <Input
                      {...props}
                      name="code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      autoFocus
                      maxLength={6}
                      placeholder="123456"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                      // Taller than the other fields and centred, because six
                      // characters in a 28rem box otherwise sit in the corner
                      // of a field the eye reads as empty. `indent` cancels the
                      // trailing letter-space so the run stays optically
                      // centred rather than pushed half a space left.
                      className={`${FIELD_CLASS} h-12 text-center indent-[0.3em] font-mono tracking-[0.3em]`}
                    />
                  )}
                </Field>
              )}

              <Button type="submit" loading={busy} disabled={cooldown > 0} className={SUBMIT_CLASS}>
                {cooldown > 0 ? `Try again in ${formatSeconds(cooldown)}` : 'Verify'}
              </Button>

              {/*
                The two ways out of the step, below a rule: they are not part of
                the form's column, and without the rule the second one reads as
                a third field's label.
              */}
              <div className="flex flex-col items-start gap-[var(--space-2)] border-t border-line pt-[var(--space-4)]">
                <button
                  type="button"
                  onClick={() => {
                    setUseRecovery((v) => !v);
                    setError(null);
                  }}
                  className="text-sm text-primary underline underline-offset-2 hover:no-underline"
                >
                  {useRecovery
                    ? 'Use your authenticator app instead'
                    : 'Use a recovery code instead'}
                </button>
                {/* The challenge token expires in minutes; someone who walked
                    away needs a way back that does not look like an error. */}
                <button
                  type="button"
                  onClick={restart}
                  className="text-sm text-primary underline underline-offset-2 hover:no-underline"
                >
                  Start over
                </button>
              </div>
            </form>
          ) : (
            <form
              onSubmit={(e) => void onSubmit(e)}
              className="flex flex-col gap-[var(--space-5)]"
              noValidate
            >
              {/*
                One error region, above the form. `data-testid` so criterion 14
                can compare the two rejection cases byte for byte.
              */}
              <ErrorNotice copy={copy} testId="login-error" />

              <Field label="Email address" required>
                {(props) => (
                  <Input
                    {...props}
                    type="email"
                    name="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-invalid={credentialsRejected || props['aria-invalid']}
                    className={FIELD_CLASS}
                  />
                )}
              </Field>

              <Field label="Password" required>
                {(props) => (
                  <Input
                    {...props}
                    type="password"
                    name="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    aria-invalid={credentialsRejected || props['aria-invalid']}
                    className={FIELD_CLASS}
                  />
                )}
              </Field>

              {needsTenantField ? (
                <Field label="Account" hint="The short name your administrator gave you.">
                  {(props) => (
                    <Input
                      {...props}
                      name="tenantSlug"
                      value={tenantSlug}
                      onChange={(e) => setTenantSlug(e.target.value)}
                      className={FIELD_CLASS}
                    />
                  )}
                </Field>
              ) : null}

              <Button type="submit" loading={busy} disabled={cooldown > 0} className={SUBMIT_CLASS}>
                {cooldown > 0 ? `Try again in ${formatSeconds(cooldown)}` : 'Sign in'}
              </Button>

              {/*
                No "forgotten your password" link. The reset flow mints a token
                and mails it, and the mailer is dormant until SMTP is configured
                — so the link led to a screen that could accept a request and
                then do nothing visible. The route still exists for a reset link
                someone is handed directly; it is just not advertised. Restore
                this once SMTP_HOST is set (docs/RUNBOOK.md).
              */}
            </form>
          )}
        </div>
      </main>

      {/*
        The decorative half. Four stacked layers, each one owned by globals.css
        because both of the things they need — `color-mix()` and
        `rgb(var(--brand-*))` — are banned in components by
        `brandColourUse.spec.ts`, and rightly: reaching the tenant's hue from a
        component is how a pair nobody measured gets invented.

        Texture, then the 5% wash, then the light. `.brand-glow` is the one that
        turns this from a tinted rectangle into a panel: it lifts the middle
        toward `surface` and settles the bottom toward `surface-alt`, so the
        lockup sits in a pool of light with the dot grid receding into the
        corners. It changes the wash's value, never its hue — so the 5% ceiling
        the wash's own docblock argues for still holds over every pixel.

        Hidden below `lg`, where the reading column takes the full width and the
        lockup above the heading carries the brand on its own.
      */}
      <div
        aria-hidden="true"
        className="relative hidden overflow-hidden border-l border-line lg:block"
      >
        <div className="dot-field absolute inset-0" />
        <div className="brand-veil absolute inset-0" />
        <div className="brand-glow absolute inset-0" />
        <div className="relative flex h-full items-center justify-center p-[var(--space-12)] xl:p-[var(--space-16)]">
          {/*
            One plate, not a disc. A circle is the right frame for a mark and
            the wrong one for a word — a wordmark inside it either shrinks to
            fit the chord or breaks across three lines — and this lockup has to
            hold both. The rings survive the change because `.halo` casts them
            as box-shadow spreads, so they follow whatever radius the call site
            asks for and cost no extra elements.

            `min-h` is what keeps the two branches the same object: a tenant
            with a logo and a tenant without get a plate of the same weight in
            the same place, rather than a composition that collapses to a line
            of text when `logoUrl` is null.
          */}
          <div className="halo flex min-h-[15rem] w-full max-w-[26rem] flex-col items-center justify-center gap-[var(--space-5)] rounded-[var(--radius-lg)] p-[var(--space-10)]">
            {branding?.logoUrl ? (
              <>
                {/*
                  `alt=""` and not the product name: the announced copy of this
                  mark is the one in the card, and a duplicate would have a
                  screen reader read the brand twice for one page.
                */}
                <img src={branding.logoUrl} alt="" className="max-h-20 w-auto max-w-full" />
                <span className="block w-10 border-t border-line" />
                {/*
                  The name is set *under* the mark rather than instead of it,
                  and deliberately quiet: a logo that already spells the product
                  out would otherwise be shouted twice. The display rank in this
                  branch belongs to the image.
                */}
                <p className="text-center text-[length:var(--text-xs)] font-semibold uppercase tracking-[var(--tracking-caps)] text-muted">
                  {branding.productName}
                </p>
              </>
            ) : (
              /*
                The `logoUrl: null` fallback — the plate frames the product name
                instead, at display rank, because with no mark the word IS the
                mark. `text-body` on the plate's plain `surface` is the pair
                `designTokens.spec.ts` measures at 17.75:1; `text-muted` is the
                one that would fall to ~4.4:1 if this text ever drifted off the
                plate onto the wash behind it. That is the Phase 05 defect in a
                new costume, and no test can see it because a runtime
                `color-mix()` resolves in the browser.
              */
              <p className="text-center text-[length:var(--text-3xl)] font-semibold leading-[var(--leading-display)] tracking-[var(--tracking-tight)] text-body">
                {branding?.productName ?? 'Connect Panel'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The one error region, shared by both steps of the sign-in.
 *
 * The glyph and the 2px rule are the redundant channels: colour alone never
 * carries the state (WCAG 1.4.1), and the icon contributes no text node, so
 * criterion 14's byte-for-byte comparison still sees exactly the title and
 * body `errorCopy` produced.
 *
 * The fill is `danger-soft` rather than `surface-alt`. Both are legible, but
 * `surface-alt` is the tenant's own second surface — on a tenant whose two
 * surfaces are close, a rejected sign-in was a card with a thin red edge and
 * otherwise no change at all. `danger-soft` is platform-fixed (§11.6), so the
 * one thing a person needs to see on this screen looks the same in every
 * tenant. `danger-text` on it measures 6.05:1 and body ink measures ~16:1 —
 * both hand-checked in `designTokens.spec.ts`'s terms, neither dependent on a
 * tenant colour.
 */
function ErrorNotice({
  copy,
  testId,
}: {
  copy: { title: string; body: string } | null;
  testId: string;
}): ReactElement | null {
  if (!copy) return null;
  return (
    <div
      role="alert"
      data-testid={testId}
      className="flex items-start gap-[var(--space-2)] rounded-[var(--radius-md)] border-l-2 border-danger bg-danger-soft p-[var(--space-3)] text-sm"
    >
      <CircleAlert
        aria-hidden="true"
        size={16}
        strokeWidth={2}
        className="mt-[2px] shrink-0 text-danger-text"
      />
      <div>
        {/* `danger-text` rather than `danger`: §16.6 wants 4.5:1 at this size
            and the pure token measures 4.44:1. */}
        <p className="font-medium text-danger-text">{copy.title}</p>
        <p className="text-body">{copy.body}</p>
      </div>
    </div>
  );
}
